import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import SchoolIcon from '@mui/icons-material/School';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CalculateIcon from '@mui/icons-material/Calculate';
import Help from '@mui/icons-material/Help';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CircularProgress from '@mui/material/CircularProgress';
import sparrowLogo from '../../assets/images/sparrow.png';
import Grid from '@mui/material/Grid';
import { useAuth } from '../../context/AuthContext';
import {
  ProjectTrackerPermissions,
  ProcurementPOPermissions,
  ProcurementRFQPermissions,
  VendorMgmtPermissions,
  ProjectAnalyticsPermissions,
  POSummaryPermissions,
  AdminPermissions,
  TutorialPermissions,
  PiplineEstimatorPermissions,
  SparrowPermissions,
  ReleaseNotesPermissions,
} from '../../utils/permissions';

const ApplicationMenuInternal = ({ navigate }) => {
  const { userPermissions, permsLoading } = useAuth();

  if (permsLoading) {
    return (
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
        <Typography variant="h6">Loading User Permissions</Typography>
      </Box>
    );
  }

  const menuItems = [
    {
      title: 'Project Tracker & Updates',
      icon: <EventNoteIcon sx={{ fontSize: 40, color: '#3b82f6' }} />,
      route: '/projecttracker',
      permissions: ProjectTrackerPermissions,
    },
    {
      title: 'Procurement - Purchase Order',
      icon: <AddShoppingCartIcon sx={{ fontSize: 40, color: '#ff4500' }} />,
      route: '/procurementrequest/po',
      permissions: ProcurementPOPermissions,
    },
    {
      title: 'Procurement - Request For Quote',
      icon: <ShoppingCartCheckoutIcon sx={{ fontSize: 40, color: '#10b981' }} />,
      route: '/procurementrequest/rfq',
      permissions: ProcurementRFQPermissions,
    },
    {
      title: 'Vendor Management',
      icon: <BusinessCenterIcon sx={{ fontSize: 40, color: '#f59e0b' }} />,
      route: '/vendormgmt',
      permissions: VendorMgmtPermissions,
    },
    {
      title: 'Project Analytics',
      icon: <AutoGraphIcon sx={{ fontSize: 40, color: '#8b5cf6' }} />,
      route: '/projectanalytics',
      permissions: ProjectAnalyticsPermissions,
    },
    {
      title: 'PO Summary',
      icon: <SummarizeIcon sx={{ fontSize: 40, color: '#ff4500' }} />,
      route: '/posummary',
      permissions: POSummaryPermissions,
    },
    {
      title: 'Pipeline Estimator',
      icon: <CalculateIcon sx={{ fontSize: 40, color: '#000000bd' }} />,
      route: '/estimator-menu',
      permissions: PiplineEstimatorPermissions,
    },
    {
      title: 'Sparrow AI',
      icon: (
        <Box
          component="img"
          src={sparrowLogo}
          alt="Sparrow"
          sx={{
            width: 40,
            height: 40,
            objectFit: 'contain',
          }}
        />
      ),
      route: '/sparrow',
      permissions: SparrowPermissions,
    },
    {
      title: 'Admin',
      icon: <AdminPanelSettingsIcon sx={{ fontSize: 40, color: '#dc64ff' }} />,
      route: '/admin',
      permissions: AdminPermissions,
    },
    {
      title: 'Tutorial Videos',
      icon: <SchoolIcon sx={{ fontSize: 40, color: '#3b82f6' }} />,
      route: '/tutorials',
      permissions: TutorialPermissions,
    },
    {
      title: 'Support Requests',
      icon: <Help sx={{ fontSize: 40, color: '#000000' }} />,
      route: '/supportrequests',
      permissions: null,
    },
    {
      title: 'Release Notes',
      icon: <AddAlertIcon sx={{ fontSize: 40, color: '#009688' }} />,
      route: '/releasenotes',
      permissions: ReleaseNotesPermissions,
    },
  ];

  const filteredMenuItems = useMemo(
    () =>
      menuItems.filter((item) =>
        item.permissions
          ? // if it has a permissions array, only show if the user has at least one
            item.permissions.some((p) => userPermissions.includes(p))
          : // if it has no permissions key, always show
            true
      ),
    [menuItems, userPermissions]
  );

  const handleCardClick = (route) => {
    navigate(route);
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3, minHeight: '80vh', maxWidth: '90vw' }}>
      <Typography variant="h5" sx={{ marginBottom: 3, textAlign: 'left' }}>
        Application Menu
      </Typography>

      <Grid container spacing={3}>
        {filteredMenuItems.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                backgroundColor: 'hsl(220, 35%, 97%)',
                border: '1px solid hsla(220, 20%, 80%, 0.4)',
                borderRadius: '8px',
                boxShadow: 0,
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s',
                },
              }}
            >
              <CardActionArea onClick={() => handleCardClick(item.route)}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {item.icon}
                  <Typography variant="h6" sx={{ color: '#111827' }}>
                    {item.title}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ApplicationMenuInternal;
